/**
 * Inline education affordance — a small `?` button next to a dimension
 * label that opens a popover with a plain-English definition + examples.
 *
 * Used in:
 *   - SearchFilters dimension headers (filter sheet)
 *   - ReviewStoryPanel dimension headers (curate themes after publish)
 *   - StoryViewPage themes block (read view)
 *
 * Content lives in lib/dimensionEducation.js so we have a single source
 * of truth across surfaces. The popover is intentionally lightweight —
 * the deeper material (carousel-style explainers) belongs on a future
 * /learn route, not blocking the user mid-task.
 */

import { HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { DIMENSION_EDU } from '@/lib/dimensionEducation';

export default function DimensionInfoButton({
  dimensionKey,
  size = 'sm',
  align = 'start',
}) {
  const entry = DIMENSION_EDU[dimensionKey];
  if (!entry) return null;

  const iconSize = size === 'lg' ? 'h-4 w-4' : 'h-3.5 w-3.5';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`About ${entry.title}`}
          className="inline-flex items-center justify-center rounded-full text-[#6B665E] hover:text-[#C05621] hover:bg-[#FBF4EB] p-0.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C05621]"
          data-testid={`dim-info-${dimensionKey}`}
          // Stop click from bubbling — the parent might be a label or the
          // chip-group container that toggles other state.
          onClick={(e) => e.stopPropagation()}
        >
          <HelpCircle className={iconSize} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        sideOffset={6}
        className="w-80 max-w-[calc(100vw-2rem)] p-4 space-y-3 bg-white border-[#E6E2D6] text-[#2D2A26]"
        data-testid={`dim-info-content-${dimensionKey}`}
      >
        <div>
          <div className="font-serif text-lg leading-tight">{entry.title}</div>
          {entry.sanskrit && (
            <div className="text-[11px] uppercase tracking-wider text-[#6B665E] mt-1">
              {entry.sanskrit}
            </div>
          )}
        </div>
        <p className="text-sm leading-relaxed text-[#2D2A26]">{entry.summary}</p>
        {Array.isArray(entry.examples) && entry.examples.length > 0 && (
          <ul className="space-y-1.5 text-sm text-[#2D2A26]">
            {entry.examples.map((line, i) => (
              <li key={i} className="flex gap-2 leading-snug">
                <span className="text-[#C05621] shrink-0">·</span>
                <span>{line}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="pt-1 border-t border-[#E6E2D6]">
          <Link
            to={`/learn#${dimensionKey}`}
            className="text-xs font-medium text-[#C05621] hover:text-[#A0461A] hover:underline underline-offset-2"
            data-testid={`dim-info-learn-more-${dimensionKey}`}
          >
            Read the full thinking →
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
